/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { Component, OnInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ObjectInstance } from "../../models/object-instance";
import { User, UserUI } from '../../models/user';
import { UserService } from '../../services/user.service';
import { UserDialogService } from '../../services/user-dialog.service';
import { Subscription } from "rxjs";
import { ConfirmDeleteDialogComponent } from "../confirm-delete-dialog.component";
import { ImageService } from 'app/services/image.service';
import { PagedResults } from 'app/models/paged-results';
import { Url_ImageById } from 'app/rest-constants';
import { DEFAULT_USER_IMAGE } from 'app/app-constants';
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from '../simple-success-error-dialog-box/simple-success-error-dialog-box.component';
import { PagePermissionType } from "../../models/page-permission-type";
import { PagePermissionService } from "../../services/page-permission.service";

export const NUMBER_OF_USERS_PER_PAGE = 10;

@Component({
  templateUrl: './user.component.html'
})
export class UserComponent implements OnInit, OnDestroy {
  @ViewChild('fileUpload')
  mFileUploadInput: ElementRef;

  @ViewChild('confirmDeleteDialog')
  mConfirmDeleteDialog: ConfirmDeleteDialogComponent;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  //Rules shown in the UI
  selectedUsers: UserUI[] = [];
  mUsers: UserUI[] = [];
  mPeople: ObjectInstance[] = [];

  filterColumnUser = null;
  filterColumnNameUser = "";
  filterStringUser = "";

  mRefreshUsersSubscription: Subscription;
  pagePermissionType = PagePermissionType;
  // paginator variables
  totalNumberOfUsers = 0;
  currentPageUser = 1;
  numberOfUsersPerPage = NUMBER_OF_USERS_PER_PAGE;

  constructor(
    private mRoute: ActivatedRoute,
    private mUserService: UserService,
    private mUserDialogService: UserDialogService,
    private mImageService: ImageService,
    public pagePermissionService: PagePermissionService
  ) {}


  ngOnInit(): void {
    //Subscribe to the resolved data in the Route
    this.mRoute.data.subscribe((data: {users: PagedResults<User>}) => {
      this.totalNumberOfUsers = data.users.size;
      this.setUsers(data.users.data);
    });

    this.mRoute.data.subscribe((data: {peopleInstances: ObjectInstance[]}) => {
      this.setPeople(data.peopleInstances);
    });

    //Subscribe to the message Service used by the Dialog to communicate that a User was updated/created and any lists need to be refreshed
    this.mRefreshUsersSubscription = this.mUserDialogService.getRefreshUsersListeners().subscribe(result => {
      this.refreshUsers();
    });
  }

  ngOnDestroy(): void {
    if (this.mRefreshUsersSubscription != null) {
      this.mRefreshUsersSubscription.unsubscribe();
    }
  }



  public imageSource(user: User): string {
    return this.mImageService.getImageSourceForUser(user);
  }



  private setUsers(users: User[]): void {
    this.mUsers = [];
    if (users != null) {
      for (const user of users) {
        const userUI = new UserUI();
        userUI.user = user;
        userUI.mSelected = false;
        this.mUsers.push(userUI);
      }
    }
    this.selectedUsers = [];
    if (users != null) {
      for (const user of users) {
        const userUI = new UserUI();
        userUI.user = user;
        userUI.mSelected = false;
        this.selectedUsers.push(userUI);
      }
    }

  }

  private setPeople(people: ObjectInstance[]): void {
    this.mPeople = (people == null ? [] : people);
  }

  private async refreshUsers(): Promise<void> {
    const users = await this.mUserService.getAllUsersForPaging(0, NUMBER_OF_USERS_PER_PAGE, 'ASC', "");

    this.setUsers(users.data);
    this.totalNumberOfUsers = users.size;
    this.currentPageUser = 1;
  }

  getPathForUserImage(userTag: User): string {
    if (userTag?.image?.id > 0) {
      return Url_ImageById(userTag.image.id);
    } else {
      return DEFAULT_USER_IMAGE;
    }
  }

  newUser(): void {
    this.mUserDialogService.createNewUser();
  }

  editUser(user: User): void {
    this.mUserDialogService.editUser(user);
  }

  uploadFileClicked(): void {
    this.mFileUploadInput.nativeElement.click();
  }

  onFileUploadChange(event: any): void {
    if (event.target.files.length === 1) {
      this.uploadUsers(event.target.files[0]);
    }
  }

  uploadUsers(file: any): void {
    const formData = new FormData();
    formData.append("file", file);

    this.mUserService.uploadUsers(formData).then(
        response => {
          console.log('Success');
        },
        error => {
          console.error(error);
        }
    );
  }

  downloadUsers(): void {
    this.mUserService.downloadUsers().then(
        response => {
           const blob = new Blob([response], { type: 'text/csv' });
           const url = window.URL.createObjectURL(blob);
           const a = document.createElement("a");

           document.body.appendChild(a);
//           a.style = 'diplay: none';
           a.href = url;
           a.download = 'objects.csv';
           a.click();
           window.URL.revokeObjectURL(url);
        },
        error => {
          console.error(error);
        }
    );
  }

  editSelectedUser(): void {
    if (this.isSingleUserSelected()) {
      for (const user of this.mUsers) {
        if (user.mSelected === true) {
          this.editUser(user.user);
          break;
        }
      }
    }
  }

  isSingleUserSelected(): boolean {
    if (this.mUsers != null && this.mUsers.length > 0) {
      let oneSelected = false;
      for (const user of this.mUsers) {
        if (user.mSelected === true) {
          if (oneSelected) {
            return false;
          } else {
            oneSelected = true;
          }
        }
      }
      return oneSelected;
    } else {
      return false;
    }
  }

  isAnyUserSelected(): boolean {
    return this.selectedUsers.some(user => user.mSelected);
  }

  isAllUsersSelected(): boolean {
    return this.selectedUsers !== null && this.selectedUsers.every(antennaInstance => antennaInstance.mSelected);
  }

  getSelectedUserNames(): string[] {
    const names: string[] = [];
    this.selectedUsers.filter(ui => ui.mSelected).forEach(ui => names.push(ui.user.firstName + " " + ui.user.lastName));
    return names;
  }


  getSelectedUserIds(): number[] {
    const ids: number[] = [];
    this.selectedUsers.filter(ui => ui.mSelected).forEach(ui => ids.push(ui.user.id));
    return ids;
  }

  deleteSelectedUsersClicked(): void {
    if (this.isAnyUserSelected()) {
      this.mConfirmDeleteDialog.show("users", this.getSelectedUserNames());
    }
  }

  deleteConfirmed(): void {
    for(const userId of this.getSelectedUserIds()) {
      this
        .mUserService
        .deleteUser(userId)
        .then(
          response => {
            this.refreshUsers();
          },
          error => {
			this.refreshUsers();
			this.handleError(error)
		  }
        );
    }
  }

  /**
   * Manually removes the account lockout for the given User so they can attempt
   * to log in again immediately. Refreshes the list afterwards so the updated
   * lock status is shown.
   */
  unlockUser(user: User): void {
    this.mUserService.unlockUser(user.id).then(
      response => {
        this.refreshUsers();
      },
      error => {
        this.refreshUsers();
        this.handleError(error);
      }
    );
  }

  pageChanged(pageNumber: number): void {
    // Show only size on this new page
    console.log("page Changed to " + pageNumber);
    this.currentPageUser = pageNumber

    this.mUserService.getAllUsersForPaging((pageNumber - 1) * NUMBER_OF_USERS_PER_PAGE, NUMBER_OF_USERS_PER_PAGE, 'ASC', this.filterStringUser).then(
      responses => {
        this.setUsers(responses.data);
        this.totalNumberOfUsers = responses.size;
      },
      error => {
        console.log("Error calling getAllUsersForPaging");
      }
    );
  }

  setFilterUser(filterColumnName: string, filterColumn: string): void {
    this.filterColumnUser = filterColumn;
    this.filterColumnNameUser = filterColumnName;
    if (!this.filterColumnUser) {
      this.filterStringUser = "";
    }
    this.filterStringChangedUser(1);
  }


  filterStringChangedUser(pageNumber?: number): void {
    if (!pageNumber) { pageNumber = 1; }
    // Show only size on this new page
    console.log("page Changed to " + pageNumber);
    this.currentPageUser = pageNumber;

    this.pageChanged(pageNumber);

  }


  handleError(error: any): void {
    console.error("UserComponent.handleError() - Error: " + error);
    if (error != null) {
      const response: Response = error;
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, response.text().toString());
    }
  }


}
